import { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";

import { useDataBlockContext } from "@/domains/datablocks/hooks/useDataBlockContext";
import { DynamicValueResponse } from "@/redux/datablocks/types";
import { FullUserResponse } from "@/redux/users/types";
import { DataBlockFormFields } from "@/domains/datablocks/types";

export const useDataBlockSubmission = (
  currentUser: FullUserResponse | null,
) => {
  const { dataBlockContextState } = useDataBlockContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dataBlockContextStateRef = useRef(dataBlockContextState);

  useEffect(() => {
    dataBlockContextStateRef.current = dataBlockContextState;
  }, [dataBlockContextState]);

  const formatValue = (value: DynamicValueResponse): DynamicValueResponse => {
    if (value && DateTime.isDateTime(value)) {
      return (
        value
          .toUTC()
          .toISO({ suppressMilliseconds: true, includeOffset: false }) + "Z"
      );
    }
    if (Array.isArray(value)) {
      const filteredValues = value.filter((v) => v != null);
      if (filteredValues.every((v) => DateTime.isDateTime(v))) {
        return filteredValues.map(
          (v) =>
            v
              .toUTC()
              .toISO({ suppressMilliseconds: true, includeOffset: false }) +
            "Z",
        );
      }
      return filteredValues;
    }
    if (value == null) {
      return null;
    }
    return value;
  };

  const waitForOngoingUpload = () => {
    return new Promise<void>((resolve) => {
      const checkUpload = () => {
        if (!dataBlockContextStateRef.current.isUploading) {
          resolve();
        } else {
          setTimeout(checkUpload, 100);
        }
      };
      checkUpload();
    });
  };

  const prepareData = (data: DataBlockFormFields, user: FullUserResponse) => {
    return {
      ...data,
      organization: user.organization?.id as number,
      fieldValues: data.fieldValues.map((fieldValue) => ({
        ...fieldValue,
        value: formatValue(fieldValue.value),
      })),
    };
  };

  const handleSubmit = async (
    data: DataBlockFormFields,
    submitFn: (data: DataBlockFormFields) => Promise<any>,
    onSuccess: (result: any) => void,
    onError: (result: any) => void,
  ) => {
    if (!currentUser) return;
    try {
      setIsSubmitting(true);

      if (dataBlockContextState.isUploading) {
        await waitForOngoingUpload();
      }

      const processedData = prepareData(data, currentUser);
      const result = await submitFn(processedData);
      onSuccess(result);
    } catch (errors) {
      onError(errors);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleSubmit,
  };
};
